/**
 * 个人设置页面
 *
 * 管理用户个人设置，目前包含 API Token 管理。
 * API Token 供程序化客户端（如 Qoder skill）使用，无需 Turnstile 验证。
 *
 * 自定义元素：`crm-settings-page`
 * 路由：`/crm/settings`
 */
import { css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { BaseElement } from '../base-element.js';
import { toast } from '../components/toast.js';
import { showConfirm } from '../components/confirm-dialog.js';
import { apiTokenApi } from '../api.js';

interface ApiTokenItem {
  id: number;
  name: string;
  token_prefix: string;
  last_used_at: number;
  created_at: number;
}

interface ApiTokenCreated {
  id: number;
  name: string;
  token: string;
  token_prefix: string;
  created_at: number;
}

@customElement('crm-settings-page')
export class CrmSettingsPage extends BaseElement {
  static styles = css`
    :host { display: block; height: 100%; }
  `;

  @state() private tokens: ApiTokenItem[] = [];
  @state() private loading = false;
  @state() private showCreateModal = false;
  @state() private newTokenName = '';
  @state() private creating = false;
  @state() private createdToken: ApiTokenCreated | null = null;
  @state() private copied = false;
  private _backdropMousedown = false;

  connectedCallback() {
    super.connectedCallback();
    this.loadTokens();
  }

  private async loadTokens() {
    this.loading = true;
    try {
      const res = await apiTokenApi.list();
      this.tokens = res.data || [];
    } catch {
      toast('加载 Token 列表失败', 'error');
    } finally {
      this.loading = false;
    }
  }

  private async handleCreate() {
    if (!this.newTokenName.trim()) {
      toast('请输入 Token 名称', 'warning');
      return;
    }
    this.creating = true;
    try {
      const token = await apiTokenApi.create({ name: this.newTokenName.trim() });
      this.createdToken = token;
      this.newTokenName = '';
      await this.loadTokens();
    } catch {
      toast('创建 Token 失败', 'error');
    } finally {
      this.creating = false;
    }
  }

  private async handleRevoke(id: number, name: string) {
    const confirmed = await showConfirm(`确认撤销 Token「${name}」？撤销后立即失效，无法恢复。`);
    if (!confirmed) return;
    try {
      await apiTokenApi.revoke(id);
      toast('Token 已撤销', 'success');
      await this.loadTokens();
    } catch {
      toast('撤销失败', 'error');
    }
  }

  private async handleCopyToken() {
    if (!this.createdToken) return;
    try {
      await navigator.clipboard.writeText(this.createdToken.token);
      this.copied = true;
      setTimeout(() => { this.copied = false; }, 2000);
      toast('已复制到剪贴板', 'success');
    } catch {
      toast('复制失败，请手动选择复制', 'error');
    }
  }

  private closeCreateModal() {
    this.showCreateModal = false;
    this.newTokenName = '';
    this.createdToken = null;
    this.copied = false;
  }

  private formatTime(ts: number): string {
    if (!ts) return '—';
    return new Date(ts * 1000).toLocaleString('zh-CN', {
      month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    });
  }

  render() {
    return html`
      <div class="max-w-3xl mx-auto p-4 sm:p-6">
        <h1 class="text-xl font-semibold text-text mb-6">个人设置</h1>

        <!-- API Token 管理 -->
        <section class="bg-surface border border-border rounded-lg p-5 mb-6">
          <div class="flex items-center justify-between mb-4">
            <div>
              <h2 class="text-base font-semibold text-text">API Token 管理</h2>
              <p class="text-sm text-text-secondary mt-1">供程序化客户端（如 Qoder skill）使用的长期认证令牌，无需 Turnstile 验证。</p>
            </div>
            <button class="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-primary text-white text-sm font-medium hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap"
                    @click=${() => this.showCreateModal = true}>
              <span class="text-lg leading-none">+</span>
              创建新 Token
            </button>
          </div>

          ${this.loading ? html`
            <p class="text-sm text-text-secondary py-4 text-center">加载中...</p>
          ` : this.tokens.length === 0 ? html`
            <p class="text-sm text-text-muted py-8 text-center">暂无 API Token</p>
          ` : html`
            <div class="space-y-2">
              ${this.tokens.map(t => html`
                <div class="flex items-center justify-between p-3 rounded-md border border-border hover:bg-bg/50 transition-colors">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2">
                      <span class="text-sm font-medium text-text">${t.name}</span>
                      <code class="text-xs text-text-muted bg-bg px-1.5 py-0.5 rounded">${t.token_prefix}...</code>
                    </div>
                    <div class="text-xs text-text-muted mt-1">
                      创建：${this.formatTime(t.created_at)}
                      <span class="mx-2">·</span>
                      最后使用：${this.formatTime(t.last_used_at)}
                    </div>
                  </div>
                  <button class="text-sm text-error hover:text-error/80 px-2 py-1 rounded transition-colors cursor-pointer bg-transparent border-none shrink-0"
                          @click=${() => this.handleRevoke(t.id, t.name)}>
                    撤销
                  </button>
                </div>
              `)}
            </div>
          `}
        </section>
      </div>

      <!-- 创建 Token 模态框 -->
      ${this.showCreateModal ? html`
        <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
             @mousedown=${(e: MouseEvent) => { this._backdropMousedown = e.target === e.currentTarget; }}
             @click=${(e: MouseEvent) => { if (this._backdropMousedown && e.target === e.currentTarget) this.closeCreateModal(); }}>
          <div class="bg-surface rounded-lg shadow-xl w-full max-w-md mx-4 p-6">
            ${this.createdToken ? html`
              <!-- 创建成功：显示明文 token -->
              <h3 class="text-lg font-semibold text-text mb-2">Token 创建成功</h3>
              <div class="bg-warning/10 border border-warning/30 rounded-md p-3 mb-4">
                <p class="text-sm text-warning font-medium mb-1">⚠ 此 Token 仅显示一次，请立即保存</p>
                <p class="text-xs text-text-secondary">关闭后将无法再次查看，如需新 Token 请重新创建。</p>
              </div>
              <div class="bg-bg rounded-md p-3 mb-4">
                <code class="text-xs text-text break-all">${this.createdToken.token}</code>
              </div>
              <div class="flex items-center gap-2 mb-4">
                <button class="flex-1 px-3 py-2 rounded-md ${this.copied ? 'bg-success text-white' : 'bg-primary text-white'} text-sm font-medium hover:opacity-90 transition-opacity cursor-pointer border-none"
                        @click=${this.handleCopyToken}>
                  ${this.copied ? '已复制 ✓' : '复制 Token'}
                </button>
                <button class="px-3 py-2 rounded-md border border-border text-text-secondary text-sm font-medium hover:bg-bg transition-colors cursor-pointer"
                        @click=${this.closeCreateModal}>
                  关闭
                </button>
              </div>
            ` : html`
              <!-- 创建表单 -->
              <h3 class="text-lg font-semibold text-text mb-4">创建 API Token</h3>
              <label class="block text-sm font-medium text-text mb-1.5">Token 名称</label>
              <input type="text"
                     class="w-full px-3 py-2 rounded-md border border-border bg-surface text-text text-sm focus:outline-none focus:border-primary mb-4"
                     placeholder="如：Qoder Skill、CI/CD"
                     maxlength="64"
                     .value=${this.newTokenName}
                     @input=${(e: Event) => { this.newTokenName = (e.target as HTMLInputElement).value; }}
                     @keydown=${(e: KeyboardEvent) => { if (e.key === 'Enter' && !this.creating) this.handleCreate(); }} />
              <div class="flex items-center gap-2">
                <button class="flex-1 px-3 py-2 rounded-md bg-primary text-white text-sm font-medium hover:opacity-90 transition-opacity cursor-pointer border-none ${this.creating ? 'opacity-60 pointer-events-none' : ''}"
                        @click=${this.handleCreate}>
                  ${this.creating ? '创建中...' : '创建'}
                </button>
                <button class="px-3 py-2 rounded-md border border-border text-text-secondary text-sm font-medium hover:bg-bg transition-colors cursor-pointer"
                        @click=${this.closeCreateModal}>
                  取消
                </button>
              </div>
            `}
          </div>
        </div>
      ` : ''}
    `;
  }
}
